import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabFeedbacks.module.css';
import MetaHead from '../components/MetaHead';
import FeedbackPriveCard from '../components/FeedbackPriveCard/FeedbackPriveCard';

/* ============================================================
   TabFeedbacks — page « Feedbacks interceptés » (le pare-feu)
   ------------------------------------------------------------
   Rend tangible le cœur de la promesse Revyo : les clients
   insatisfaits captés en privé au lieu de partir sur Google.

   Purement présentationnel : données fournies par
   FeedbacksInterceptesContainer.

   Props :
     - feedbacks  : [{ id, texte, prenom, satisfaction, rps_score, telephone_client, created_at }]
     - countMois  : number (feedbacks interceptés le mois en cours, Europe/Paris)
     - countTotal : number (tous)
     - loading    : bool
     - error      : string | null
     - onRetry()  : refetch
   ============================================================ */
export default function TabFeedbacks({
  feedbacks = [],
  countMois = 0,
  countTotal = 0,
  loading = false,
  error = null,
  onRetry,
}) {
  const { t } = useTranslation('tabs');

  return (
    <div className={styles.wrap}>
      <MetaHead page="feedbacks" />

      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1>
            <span className={styles.titleEmoji}>{t('feedbacksInterceptes.title_emoji')}</span>
            {t('feedbacksInterceptes.title')}
          </h1>
          <p>{t('feedbacksInterceptes.subtitle')}</p>
        </div>
      </header>

      {loading ? (
        <div className={styles.loading}>{t('feedbacksInterceptes.loading')}</div>
      ) : error ? (
        <div className={styles.error}>
          {error}
          {onRetry && (
            <button type="button" className={styles.errorRetry} onClick={onRetry}>
              {t('feedbacksInterceptes.error_retry')}
            </button>
          )}
        </div>
      ) : feedbacks.length === 0 ? (
        /* État vide (compte à 0) — pas de compteur « faux zéro » démoralisant
           (même doctrine que le cockpit d'activation #187), juste un message positif. */
        <div className={styles.empty}>
          <div className={styles.emptyEmoji} aria-hidden="true">💬</div>
          <div className={styles.emptyTitle}>{t('feedbacksInterceptes.empty_title')}</div>
          <p className={styles.emptyText}>{t('feedbacksInterceptes.empty_text')}</p>
        </div>
      ) : (
        <>
          {/* ===== Compteur pare-feu ===== */}
          <section className={styles.counterCard}>
            <div className={styles.counterBlob} aria-hidden="true" />
            <span className={styles.counterShield} aria-hidden="true">💬</span>
            <div className={styles.counterText}>
              <p className={styles.counterMonth}>
                {t('feedbacksInterceptes.counter_month', { count: countMois })}
              </p>
              <p className={styles.counterTotal}>
                {t('feedbacksInterceptes.counter_total', { count: countTotal })}
              </p>
              <p className={styles.counterCaption}>
                {t('feedbacksInterceptes.counter_caption')}
              </p>
            </div>
          </section>

          {/* ===== Liste ===== */}
          <section className={styles.listBlock}>
            <header className={styles.blockHead}>
              <h2>{t('feedbacksInterceptes.list_title')}</h2>
              <span className={styles.blockCount}>{feedbacks.length}</span>
            </header>
            <div className={styles.list}>
              {feedbacks.map((f) => (
                <FeedbackPriveCard key={f.id} feedback={f} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
